import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppBrowserSettings } from "./AppBrowserSettings";
import { AppContact } from "./AppIndex";
import { AppSearch } from "./AppSearch";
import { AppViews } from "./AppViews";
import { AppIcon, AppIcons } from "./AppIcons";
import { AppMenuRules } from "./AppMenu";

// AppPalette - the command palette (owner-approved design item 13, 2026-09-30: "Yes - the contact palette"). Ctrl+K
// (Cmd+K on a Mac) opens one box and a list: the contacts of the loaded index, the views, and a few actions.
//
// ⛔ DECISION 9: EVERY ITEM ONLY NAVIGATES OR OPENS UI. A contact opens its page exactly as its card does (`AppChatLink`);
// a view is a route; an action opens the AI panel, the "Ask about everyone" box, starts the CSV download of the owner's
// own chat, switches the theme or shows the shortcuts. There is no "send", no "reply with...", nothing that queues a
// message - `AppKeys.test.ts` greps this file and `AppKeys.ts` for every sender name.
//
// ⛔ WHAT IS REMEMBERED: the last 8 contact HREFS opened (`wac.recent` in localStorage) - never a name, a number or a
// message. Every access is wrapped: the accessor throws in private browsing and under a blocked-storage policy.
//
// ⛔ MARKUP: names come from the subscriber's own index, but a contact's name is still text somebody else chose, so
// every title and sub-line is ESCAPED (`kHTMLCore`) before it becomes markup; icons are constants of ours.
//
// Split: `AppChatLink` (the card's link rule), `AppPaletteRules` (pure: ranking, fold, recent, markup), `AppPaletteModel`
// (the list's keyboard state, no DOM), `AppPaletteRecentStore` (localStorage), `AppPaletteView` (the <dialog>).

/** The link a chat card opens - ONE rule, shared by the palette and Alt+Up/Down. Pinned to `AppViews` by a test. */
export class AppChatLink {
    /** `#c/<href>` for a chat with a page, `#n/<chat id>` for a chat that began after the last rebuild (as `AppViews.Card`). */
    public static Of = (One: AppContact): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (One.IsNew === true) { return "#" + AppViews.NewRoute + "/" + encodeURIComponent(One.ChatId); }
            return "#c/" + encodeURIComponent(One.Href);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatLink.Of");
            return "";
        }
    };

    /** The chat link of the page an address shows (`#c/<href>/m-...` keeps only the chat), or "" off a chat page. */
    public static CurrentOf = (Hash: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Parts: string[] = nz(Hash).replace("#", "").split("/");
            if (Parts.length < 2 || Parts[1].length === 0) { return ""; }
            if (Parts[0] !== "c" && Parts[0] !== AppViews.NewRoute) { return ""; }
            return "#" + Parts[0] + "/" + encodeURIComponent(AppChatLink.Decoded(Parts[1]));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatLink.CurrentOf");
            return "";
        }
    };

    /** The page href a `#c/...` address opens, or "" - what the recent list keeps. */
    public static HrefOf = (Hash: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Parts: string[] = nz(Hash).replace("#", "").split("/");
            if (Parts.length < 2 || Parts[0] !== "c") { return ""; }
            return AppChatLink.Decoded(Parts[1]);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatLink.HrefOf");
            return "";
        }
    };

    /** `decodeURIComponent` that answers the text as it came for a malformed escape (a hand-typed address). */
    private static Decoded = (Part: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (/%(?![0-9a-fA-F]{2})/.test(Part) === true) { return Part; }
            return decodeURIComponent(Part);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatLink.Decoded");
            return "";
        }
    };
}

/** One row of the palette. `Link` = a route to go to; `Act` = one of `AppPaletteRules`' action words. */
export class AppPaletteItem {
    public Kind: string = "";
    public Title: string = "";
    public Sub: string = "";
    public Link: string = "";
    public Act: string = "";
    public Icon: AppIcon | null = null;
    public Score: number = 0;
    public Order: number = 0;
}

/** What the palette may offer right now - read from the page when it opens. */
export class AppPaletteContext {
    /** Every contact of the loaded index (any order - the palette sorts a copy newest first). */
    public Contacts: AppContact[] = [];
    /** Recently opened page hrefs, newest first (`AppPaletteRecentStore`). */
    public Recent: string[] = [];
    public Loaded: boolean = false;
    public Sent: boolean = false;
    public AskAll: boolean = false;
    public CanAssist: boolean = false;
    public CanExport: boolean = false;
}

export class AppPaletteRules {
    public static readonly MaxResults: number = 50;
    public static readonly MaxRecent: number = 8;
    public static readonly RecentKey: string = "wac.recent";
    public static readonly MaxHref: number = 400;

    public static readonly KindContact: string = "contact";
    public static readonly KindView: string = "view";
    public static readonly KindAction: string = "action";

    // ⛔ The only actions there are. Each opens UI or navigates; none sends (decision 9).
    public static readonly ActAssist: string = "assist";
    public static readonly ActAskAll: string = "askall";
    public static readonly ActExport: string = "export";
    public static readonly ActThemeLight: string = "theme-light";
    public static readonly ActThemeDark: string = "theme-dark";
    public static readonly ActThemeSystem: string = "theme-system";
    public static readonly ActHelp: string = "help";

    public static readonly PrefixScore: number = 3;
    public static readonly WordScore: number = 2;
    public static readonly ContainsScore: number = 1;

    /**
     * The rows for `Query`. An empty box: recently opened chats first, then the views, the actions and every other chat
     * newest first. A query: everything that matches, ranked prefix > word start > contains, ties in that same order.
     */
    public static Results = (Query: string, Ctx: AppPaletteContext): AppPaletteItem[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Want: string = AppSearch.Fold(Query).trim();
            const People: AppPaletteItem[] = AppPaletteRules.ContactItems(Ctx);
            const Rest: AppPaletteItem[] = AppPaletteRules.ViewItems(Ctx).concat(AppPaletteRules.ActionItems(Ctx));
            if (Want.length === 0) { return AppPaletteRules.Cap(AppPaletteRules.EmptyOrder(People, Rest, Ctx.Recent)); }
            const Out: AppPaletteItem[] = [];
            const All: AppPaletteItem[] = People.concat(Rest);
            for (let At: number = 0; At < All.length; At++) {
                const One: AppPaletteItem = All[At];
                One.Order = At;
                One.Score = AppPaletteRules.ScoreOf(Want, One);
                if (One.Score > 0) { Out.push(One); }
            }
            Out.sort(AppPaletteRules.ByScore);
            return AppPaletteRules.Cap(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.Results");
            return [];
        }
    };

    /** How well `Want` (folded) matches one text: 3 its start, 2 the start of a word in it, 1 anywhere, 0 not at all. */
    public static TextScore = (Want: string, Text: string): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            const Hay: string = AppSearch.Fold(Text);
            if (Want.length === 0 || Hay.length === 0) { return 0; }
            if (Hay.indexOf(Want) === 0) { return AppPaletteRules.PrefixScore; }
            for (const Word of Hay.split(/[^\p{L}\p{N}]+/u)) {
                if (Word.length > 0 && Word.indexOf(Want) === 0) { return AppPaletteRules.WordScore; }
            }
            if (Hay.indexOf(Want) > 0) { return AppPaletteRules.ContainsScore; }
            return 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.TextScore");
            return 0;
        }
    };

    /**
     * A row's score. The TITLE may reach 3; a contact's relationship or number, and a view's or action's sub-line, count
     * at most as a word start - so "Anna" typed puts Anna Karlsson above "Anna's brother" in somebody's relationship.
     */
    public static ScoreOf = (Want: string, One: AppPaletteItem): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            let Best: number = AppPaletteRules.TextScore(Want, One.Title);
            const Side: number = Math.min(AppPaletteRules.WordScore, AppPaletteRules.TextScore(Want, One.Sub));
            if (Side > Best) { Best = Side; }
            const Digits: string = AppViews.PhoneDigits(Want);
            if (Best === 0 && Digits.length > 0 && One.Kind === AppPaletteRules.KindContact
                && One.Sub.replace(/\D/g, "").indexOf(Digits) >= 0) { Best = AppPaletteRules.ContainsScore; }
            return Best;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.ScoreOf");
            return 0;
        }
    };

    /** The recent list after `Href` was opened: it first, no duplicate, at most 8. "" or an unsafe href changes nothing. */
    public static Remember = (List: string[], Href: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return List; }
        try {
            if (AppPaletteRules.HrefOk(Href) === false) { return List.slice(0, AppPaletteRules.MaxRecent); }
            const Out: string[] = [Href];
            for (const One of List) {
                if (One !== Href && Out.length < AppPaletteRules.MaxRecent) { Out.push(One); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.Remember");
            return List;
        }
    };

    /** The stored recent list, strictly: a JSON array of plausible hrefs; anything else is an empty list. */
    public static ParseRecent = (Text: string | null): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            if (Text === null || Text.length === 0) { return []; }
            if (/^\s*\[/.test(Text) === false) { return []; }
            const Raw: unknown = JSON.parse(Text);
            if (Array.isArray(Raw) === false) { return []; }
            const Out: string[] = [];
            for (const One of Raw as unknown[]) {
                if (typeof One !== "string" || AppPaletteRules.HrefOk(One) === false || Out.indexOf(One) >= 0) { continue; }
                if (Out.length < AppPaletteRules.MaxRecent) { Out.push(One); }
            }
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: the stored text is browser storage anybody can edit; unreadable = no recent chats.
            return [];
        }
    };

    /** An href as the index writes it: a relative path, no markup characters, no scheme. */
    public static HrefOk = (Href: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Href.length === 0 || Href.length > AppPaletteRules.MaxHref) { return false; }
            if (/[<>"'\s\\]/.test(Href) === true) { return false; }
            return /^[a-z][a-z0-9+.-]*:/i.test(Href) === false && Href.indexOf("//") !== 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.HrefOk");
            return false;
        }
    };

    /** The list's markup - titles and sub-lines ESCAPED, icons ours. `Active` is the highlighted row. */
    public static ListHtml = (Items: AppPaletteItem[], Active: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = [];
            for (let At: number = 0; At < Items.length; At++) {
                const One: AppPaletteItem = Items[At];
                let Cls: string = "palopt";
                let Selected: string = "false";
                if (At === Active) { Cls = "palopt on"; Selected = "true"; }
                let Icon: string = "";
                if (One.Icon !== null) { Icon = AppIcons.Svg(One.Icon, 18); }
                let Sub: string = "";
                // (2026-11-02, owner) explanations only under a contact - actions and views are their own words.
                if (One.Sub.length > 0 && One.Kind === AppPaletteRules.KindContact) { Sub = "<span class=\"pals\">" + kHTMLCore.EscapeHtml(One.Sub) + "</span>"; }
                Out.push("<li role=\"option\" id=\"" + AppPaletteRules.OptionId(At) + "\" class=\"" + Cls + "\" aria-selected=\""
                    + Selected + "\" data-wac-pal=\"" + String(At) + "\"><span class=\"palic\">" + Icon + "</span><span class=\"paltx\">"
                    + "<span class=\"palt\">" + kHTMLCore.EscapeHtml(One.Title) + "</span>" + Sub + "</span><span class=\"palk\">"
                    + kHTMLCore.EscapeHtml(AppPaletteRules.KindWord(One.Kind)) + "</span></li>");
            }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.ListHtml");
            return "";
        }
    };

    /** The status line under the list (read aloud): how many rows, or that nothing matched. */
    public static StatusText = (Query: string, Count: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Count === 0 && Query.trim().length > 0) { return "Nothing matches \"" + Query.trim() + "\""; }
            if (Count === 0) { return "No chats yet"; }
            if (Count === 1) { return "1 result"; }
            return Count.toLocaleString() + " results";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.StatusText");
            return "";
        }
    };

    public static OptionId = (At: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "wac-pal-" + String(At);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.OptionId");
            return "";
        }
    };

    private static KindWord = (Kind: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Kind === AppPaletteRules.KindContact) { return "Chat"; }
            if (Kind === AppPaletteRules.KindView) { return "View"; }
            return "Action";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.KindWord");
            return "";
        }
    };

    /** Every contact as a row, newest first (the chat list's order); sub-line = relationship and number. */
    private static ContactItems = (Ctx: AppPaletteContext): AppPaletteItem[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppPaletteItem[] = [];
            for (const One of AppViews.NewestFirst(Ctx.Contacts)) {
                const Row: AppPaletteItem = new AppPaletteItem();
                Row.Kind = AppPaletteRules.KindContact;
                Row.Title = One.Title;
                const Bits: string[] = [];
                if (One.Relationship.length > 0) { Bits.push(One.Relationship); }
                if (One.PersonKey.indexOf("+") === 0) { Bits.push(One.PersonKey); }
                Row.Sub = Bits.join("  ·  ");
                Row.Link = AppChatLink.Of(One);
                Row.Act = One.Href;
                Row.Icon = AppIcons.Person;
                Out.push(Row);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.ContactItems");
            return [];
        }
    };

    private static ViewItems = (Ctx: AppPaletteContext): AppPaletteItem[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            if (Ctx.Loaded === false) { return []; }
            const Rows: string[][] = [["Chats", "#chats"], ["Overview", "#overview"], ["Tasks", "#tasks"], ["Revenue", "#money"]];
            if (Ctx.Sent === true) { Rows.push(["Sent", "#sent"]); }
            const Out: AppPaletteItem[] = [];
            for (const One of Rows) {
                const Row: AppPaletteItem = AppPaletteRules.Row(AppPaletteRules.KindView, One[0], "Go to " + One[0], AppIcons.Forward);
                Row.Link = One[1];
                Out.push(Row);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.ViewItems");
            return [];
        }
    };

    private static ActionItems = (Ctx: AppPaletteContext): AppPaletteItem[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppPaletteItem[] = [];
            if (Ctx.CanAssist === true) { Out.push(AppPaletteRules.Action("Open the AI panel", "Ask AI about this chat", AppIcons.Spark, AppPaletteRules.ActAssist)); }
            if (Ctx.AskAll === true && Ctx.Loaded === true) {
                Out.push(AppPaletteRules.Action("Ask about everyone", "One question across all your chats", AppIcons.Spark, AppPaletteRules.ActAskAll));
            }
            if (Ctx.CanExport === true) {
                Out.push(AppPaletteRules.Action("Export this chat", "Download the whole conversation as a web page", AppIcons.Download, AppPaletteRules.ActExport));
            }
            // ⭐ NEW CHAT (`AppNewChat`): a NAVIGATION row (`KindView` + `Link`) - the number is typed and checked there; ⛔ nothing is sent from here.
            if (Ctx.Loaded === true) {
                const Start: AppPaletteItem = AppPaletteRules.Row(AppPaletteRules.KindView, "New chat", "Start a chat with a phone number", AppIcons.NewChat);
                Start.Link = "#newchat"; Out.push(Start);
            }
            Out.push(AppPaletteRules.Action("Theme: light", "Switch theme", AppIcons.Sun, AppPaletteRules.ActThemeLight));
            Out.push(AppPaletteRules.Action("Theme: dark", "Switch theme", AppIcons.Moon, AppPaletteRules.ActThemeDark));
            Out.push(AppPaletteRules.Action("Theme: match this device", "Switch theme", AppIcons.Monitor, AppPaletteRules.ActThemeSystem));
            Out.push(AppPaletteRules.Action("Keyboard shortcuts", "Show every shortcut", AppIcons.Settings, AppPaletteRules.ActHelp));
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.ActionItems");
            return [];
        }
    };

    private static Action = (Title: string, Sub: string, Icon: AppIcon, Act: string): AppPaletteItem => {
        if (getCoreUtils().KErrorMode === true) { return new AppPaletteItem(); }
        try {
            const Row: AppPaletteItem = AppPaletteRules.Row(AppPaletteRules.KindAction, Title, Sub, Icon);
            Row.Act = Act;
            return Row;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.Action");
            return new AppPaletteItem();
        }
    };

    public static Row = (Kind: string, Title: string, Sub: string, Icon: AppIcon): AppPaletteItem => {
        if (getCoreUtils().KErrorMode === true) { return new AppPaletteItem(); }
        try {
            const Row: AppPaletteItem = new AppPaletteItem();
            Row.Kind = Kind;
            Row.Title = Title;
            Row.Sub = Sub;
            Row.Icon = Icon;
            return Row;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.Row");
            return new AppPaletteItem();
        }
    };

    /** The empty box: recent chats (in the order opened), then views and actions, then every other chat. */
    private static EmptyOrder = (People: AppPaletteItem[], Rest: AppPaletteItem[], Recent: string[]): AppPaletteItem[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const First: AppPaletteItem[] = [];
            for (const Href of Recent) {
                for (const One of People) {
                    if (One.Act === Href && First.indexOf(One) < 0) { First.push(One); }
                }
            }
            const Others: AppPaletteItem[] = [];
            for (const One of People) { if (First.indexOf(One) < 0) { Others.push(One); } }
            return First.concat(Rest).concat(Others);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.EmptyOrder");
            return [];
        }
    };

    private static Cap = (Items: AppPaletteItem[]): AppPaletteItem[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            return Items.slice(0, AppPaletteRules.MaxResults);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRules.Cap");
            return [];
        }
    };

    /** Higher score first; a tie keeps the order the rows were made in (chats newest first, then views, then actions). */
    private static ByScore = (A: AppPaletteItem, B: AppPaletteItem): number => {
        const Broken: boolean = getCoreUtils().KErrorMode;
        const Zero: number = 0;
        if (Broken === true) { return Zero; }
        try {
            const Gap: number = B.Score - A.Score;
            if (A.Score !== B.Score) { return Gap; }
            const Keep: number = A.Order - B.Order;
            return Keep;
        } catch (e) {
            const Where: string = "AppPaletteRules.ByScore";
            getCoreUtils().KGlobalErrorHandler(e, Where);
            return Zero;
        }
    };
}

/** The list's keyboard state: which row is highlighted. No DOM, so the keys are tested directly. */
export class AppPaletteModel {
    public Items: AppPaletteItem[] = [];
    public Active: number = -1;

    public Set = (Items: AppPaletteItem[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Items = Items;
            this.Active = -1;
            if (Items.length > 0) { this.Active = 0; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteModel.Set");
        }
    };

    /** Down (+1) / Up (-1), wrapping round the ends. */
    public Move = (Dir: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Count: number = this.Items.length;
            if (Count === 0) { this.Active = -1; return; }
            this.Active = (Math.max(0, this.Active) + Dir + Count) % Count;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteModel.Move");
        }
    };

    /** Home (first) / End (last). */
    public Edge = (Last: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Items.length === 0) { this.Active = -1; return; }
            this.Active = 0;
            if (Last === true) { this.Active = this.Items.length - 1; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteModel.Edge");
        }
    };

    public Point = (At: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (At >= 0 && At < this.Items.length) { this.Active = At; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteModel.Point");
        }
    };

    /** The highlighted row, or null for an empty list. */
    public Picked = (): AppPaletteItem | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (this.Active < 0 || this.Active >= this.Items.length) { return null; }
            return this.Items[this.Active];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteModel.Picked");
            return null;
        }
    };
}

/** `wac.recent` in localStorage - every access wrapped (private browsing, a blocked-storage policy and quota throw). */
export class AppPaletteRecentStore {
    public static Read = (): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            // Storage the browser refuses = the shared class's "nothing stored" = no recent chats; the palette still lists everyone.
            return AppPaletteRules.ParseRecent(AppBrowserSettings.Local().ReadText(AppPaletteRules.RecentKey).Value);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRecentStore.Read");
            return [];
        }
    };

    public static Write = (List: string[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // A refused write is answered by the shared class as a plain outcome: the next visit just starts without recent chats.
            AppBrowserSettings.Local().WriteJson(AppPaletteRules.RecentKey, List);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteRecentStore.Write");
        }
    };
}

/** Told when a row is chosen - `AppKeyDispatch.Run`. */
export interface IAppPalettePick {
    Pick(Item: AppPaletteItem): void;
}

/**
 * The palette as a native modal `<dialog>` (the rest of the page is inert while it is open; Esc closes it).
 * ARIA: the input is a combobox over a listbox, the highlighted row is its `aria-activedescendant`.
 * ⚠ Built once, on first open, from constant markup of ours; the rows are `AppPaletteRules.ListHtml` (escaped).
 */
export class AppPaletteView {
    public static readonly Id: string = "wac-palette";
    public static readonly InputId: string = "wac-pal-q";
    public static readonly ListId: string = "wac-pal-list";
    public static readonly StatusId: string = "wac-pal-status";

    private Dialog: HTMLDialogElement | null = null;
    private Input: HTMLInputElement | null = null;
    private List: HTMLElement | null = null;
    private Status: HTMLElement | null = null;
    private Model: AppPaletteModel = new AppPaletteModel();
    private Ctx: AppPaletteContext = new AppPaletteContext();
    private Back: HTMLElement | null = null;
    private Chosen: boolean = false;
    private Picker: IAppPalettePick;

    public constructor(Picker: IAppPalettePick) {
        this.Picker = Picker;
    }

    public IsOpen = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return this.Dialog !== null && this.Dialog.open === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.IsOpen");
            return false;
        }
    };

    public Open = (Ctx: AppPaletteContext): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.IsOpen() === true) { return; }
            this.Build();
            if (this.Dialog === null || this.Input === null) { return; }
            this.Ctx = Ctx;
            this.Chosen = false;
            this.Back = document.activeElement as HTMLElement | null;
            this.Input.value = "";
            this.Render();
            if (typeof this.Dialog.showModal === "function") { this.Dialog.showModal(); } else { this.Dialog.setAttribute("open", ""); }
            this.Input.focus();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.Open");
        }
    };

    /**
     * The close step shared by every native-dialog host (`AppKeyHelpView.Close` too): closed, or the `open`
     * attribute removed and the closed-step run when the dialog has no `close` (a host's own `IsOpen`/`OnClosed`
     * are handed in, so each host keeps its own state and its own diagnostic label).
     */
    public static CloseShared = (Dialog: HTMLDialogElement | null, IsOpen: () => boolean, OnClosed: () => void, Label: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Dialog === null || IsOpen() === false) { return; }
            if (typeof Dialog.close === "function") { Dialog.close(); } else { Dialog.removeAttribute("open"); OnClosed(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Label);
        }
    };

    public Close = (): void => {
        AppPaletteView.CloseShared(this.Dialog, this.IsOpen, this.OnClosed, "AppPaletteView.Close");
    };

    private Build = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Dialog !== null) { return; }
            const Made: HTMLDialogElement = document.createElement("dialog");
            Made.id = AppPaletteView.Id;
            Made.className = "wac-pal";
            Made.setAttribute("aria-label", "Go to a chat, a view or a command");
            Made.innerHTML = AppPaletteView.Shell();
            document.body.appendChild(Made);
            this.Dialog = Made;
            this.Input = document.getElementById(AppPaletteView.InputId) as HTMLInputElement | null;
            this.List = document.getElementById(AppPaletteView.ListId);
            this.Status = document.getElementById(AppPaletteView.StatusId);
            if (this.Input === null || this.List === null) { return; }
            this.Input.addEventListener("input", this.Render);
            this.Input.addEventListener("keydown", this.OnKey);
            this.List.addEventListener("click", this.OnListClick);
            this.List.addEventListener("mousemove", this.OnListHover);
            Made.addEventListener("click", this.OnBackdrop);
            Made.addEventListener("close", this.OnClosed);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.Build");
        }
    };

    /** The dialog's constant inside - no user data. */
    public static Shell = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<div class=\"palbox\"><span class=\"palsearch\">" + AppIcons.Svg(AppIcons.Search, 18) + "</span>"
                + "<input id=\"" + AppPaletteView.InputId + "\" type=\"text\" role=\"combobox\" aria-expanded=\"true\" aria-autocomplete=\"list\""
                + " aria-controls=\"" + AppPaletteView.ListId + "\" aria-label=\"Go to a chat, a view or a command\" autocomplete=\"off\""
                + " spellcheck=\"false\"></div>"
                + "<ul id=\"" + AppPaletteView.ListId + "\" class=\"pallist\" role=\"listbox\" aria-label=\"Results\"></ul>"
                + "<p id=\"" + AppPaletteView.StatusId + "\" class=\"palstatus\" aria-live=\"polite\"></p>"
                + "<p class=\"palhint\">" + AppIcons.ArrowKey(true)
                + AppIcons.ArrowKey(false) + " to move"
                + "  ·  <kbd>Enter</kbd> to open  ·  <kbd>Esc</kbd> to close</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.Shell");
            return "";
        }
    };

    private Render = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Input === null || this.List === null) { return; }
            this.Model.Set(AppPaletteRules.Results(this.Input.value, this.Ctx));
            this.Paint();
            if (this.Status !== null) { this.Status.textContent = AppPaletteRules.StatusText(this.Input.value, this.Model.Items.length); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.Render");
        }
    };

    /** The rows and the active one - redrawn whole (at most 50 rows). */
    private Paint = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Input === null || this.List === null) { return; }
            this.List.innerHTML = AppPaletteRules.ListHtml(this.Model.Items, this.Model.Active);
            let Active: string = "";
            if (this.Model.Active >= 0) { Active = AppPaletteRules.OptionId(this.Model.Active); }
            this.Input.setAttribute("aria-activedescendant", Active);
            const Row: HTMLElement | null = document.getElementById(Active);
            if (Row !== null && typeof Row.scrollIntoView === "function") { Row.scrollIntoView({ block: "nearest" }); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.Paint");
        }
    };

    private OnKey = (Ev: KeyboardEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Key: string = Ev.key;
            if ((Ev.ctrlKey === true || Ev.metaKey === true) && (Key === "k" || Key === "K")) { Ev.preventDefault(); this.Close(); return; }
            if (Key === "ArrowDown" || Key === "ArrowUp") {
                Ev.preventDefault();
                let Dir: number = 1;
                if (Key === "ArrowUp") { Dir = -1; }
                this.Model.Move(Dir);
                this.Paint();
                return;
            }
            if (Key === "Enter" && Ev.isComposing !== true) { Ev.preventDefault(); this.Choose(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.OnKey");
        }
    };

    private OnListClick = (Ev: MouseEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const At: number = AppPaletteView.RowAt(Ev);
            if (At < 0) { return; }
            this.Model.Point(At);
            this.Choose();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.OnListClick");
        }
    };

    private OnListHover = (Ev: MouseEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const At: number = AppPaletteView.RowAt(Ev);
            if (At < 0 || At === this.Model.Active) { return; }
            this.Model.Point(At);
            this.Paint();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.OnListHover");
        }
    };

    private static RowAt = (Ev: MouseEvent): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            const Target: Element | null = Ev.target as Element | null;
            if (Target === null || typeof Target.closest !== "function") { return -1; }
            const Row: Element | null = Target.closest("[data-wac-pal]");
            if (Row === null) { return -1; }
            const At: number = parseInt(nz(Row.getAttribute("data-wac-pal")), 10);
            if (isNaN(At) === true) { return -1; }
            return At;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.RowAt");
            return -1;
        }
    };

    /** A click on the dim backdrop (the dialog element itself, outside its box) closes. */
    private OnBackdrop = (Ev: MouseEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Ev.target === this.Dialog) { this.Close(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.OnBackdrop");
        }
    };

    /** The button of the menu `El` sits in (`[data-wac-menu="<the popover's id>"]`, see `AppMenu`), or null. */
    private static MenuButtonOf = (El: HTMLElement): HTMLElement | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            for (let At: HTMLElement | null = El; At !== null; At = At.parentElement) {
                if (nz(At.id).length === 0 || /^[A-Za-z][\w-]*$/.test(At.id) === false) { continue; }
                const Button: HTMLElement | null = document.querySelector("[" + AppMenuRules.Attr + "=\"" + At.id + "\"]");
                if (Button !== null) { return Button; }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.MenuButtonOf");
            return null;
        }
    };

    /** Closed WITHOUT a choice (Esc, the backdrop): focus goes back where it was. A choice moves focus itself. */
    private OnClosed = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Chosen === false && this.Back !== null && this.Back.isConnected === true) {
                this.Back.focus();
                // ⭐ OPENED OVER A MENU (audit 2026-10-12): the menu shut when the finder opened, so its item - where the
                // focus was - is hidden and cannot take the focus back; it fell to the page. The menu's own button can.
                if (document.activeElement !== this.Back) {
                    const Opener: HTMLElement | null = AppPaletteView.MenuButtonOf(this.Back);
                    if (Opener !== null) { Opener.focus(); }
                }
            }
            this.Back = null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.OnClosed");
        }
    };

    /** The highlighted row: close first, then hand it over (`AppKeyDispatch.Run` - navigation or UI only). */
    private Choose = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Item: AppPaletteItem | null = this.Model.Picked();
            if (Item === null) { return; }
            this.Chosen = true;
            this.Close();
            this.Picker.Pick(Item);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPaletteView.Choose");
        }
    };
}
